import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  Calendar,
  Clock,
  MessageCircle,
  ArrowRight
} from 'lucide-react'

export const DemoPage: React.FC = () => {
  const { t } = useTranslation('demo')

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-32 bg-gradient-to-br from-kylix-primary to-kylix-secondary text-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-8">
                <Calendar className="w-12 h-12 text-white" />
              </div>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold mb-6"
            >
              {t('demo.hero.title')}
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-white/90 mb-8 leading-relaxed max-w-3xl mx-auto"
            >
              {t('demo.hero.description')}
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/contact">
                <button className="btn-primary bg-white text-kylix-primary hover:bg-gray-100 px-8 py-4 text-lg">
                  <Calendar className="w-5 h-5" />
                  {t('demo.hero.scheduleButton')}
                </button>
              </Link>
              
              <Link to="/">
                <button className="px-8 py-4 text-lg rounded-lg font-semibold bg-transparent border-2 border-white text-white hover:bg-white hover:text-kylix-primary transition-colors inline-flex items-center gap-2">
                  <ArrowRight className="w-5 h-5 rotate-180" />
                  {t('demo.hero.backButton')}
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Status Section */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-kylix-background to-white rounded-2xl border border-gray-200 p-12 text-center"
            >
              <div className="w-16 h-16 bg-kylix-secondary rounded-full flex items-center justify-center mx-auto mb-8">
                <Clock className="w-8 h-8 text-white" />
              </div>
              
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {t('demo.status.title')}
              </h2>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {t('demo.status.description')}
              </p>
              
              <div className="bg-kylix-secondary/10 border border-kylix-secondary/20 rounded-xl p-6 mb-8">
                <h3 className="font-semibold text-gray-900 mb-4">{t('demo.status.comingSoonTitle')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-kylix-secondary rounded-full"></div>
                    <span>{t('demo.status.features.sandbox')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-kylix-secondary rounded-full"></div>
                    <span>{t('demo.status.features.demoData')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-kylix-secondary rounded-full"></div>
                    <span>{t('demo.status.features.tutorials')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-kylix-secondary rounded-full"></div>
                    <span>{t('demo.status.features.allFeatures')}</span>
                  </div>
                </div>
              </div>
              
              <Link to="/contact">
                <button className="btn-primary text-lg px-8 py-4">
                  <MessageCircle className="w-5 h-5" />
                  {t('demo.status.scheduleButton')}
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}